import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft } from "lucide-react";
import { Input } from "@/components/ui/input";

const BotDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // Mock data - in real app, fetch based on id
  const botType = id === "1" || id === "2" ? "automated" : "on-demand";

  const renderAutomatedBotContent = () => (
    <>
      <Card>
        <CardContent className="p-6 space-y-4">
          <h3 className="font-semibold">Schedule Configuration</h3>
          <ul className="space-y-2 text-sm">
            <li>• Trigger: 48 hours before appointment</li>
            <li>• Retry Strategy: Every 4 hours, max 3 tries</li>
            <li>• Active Hours: 9 AM - 7 PM</li>
          </ul>
          <Button>Edit Configuration</Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-4">
          <h3 className="font-semibold">Script Configuration</h3>
          <ul className="space-y-2 text-sm">
            <li>• Voice: Female</li>
            <li>• Languages: English, Spanish</li>
          </ul>
          <div className="flex flex-wrap gap-2">
            <Button>Edit Script</Button>
            <Button variant="outline">Test Call</Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-6">
            <h3 className="font-semibold mb-4">Today's Queue</h3>
            <div className="space-y-2 text-sm">
              <div>45 scheduled</div>
              <div>12 completed</div>
              <div>2 failed</div>
              <Button className="mt-4">View Details</Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h3 className="font-semibold mb-4">Analytics</h3>
            <div className="space-y-2 text-sm">
              <div>Success Rate: 87%</div>
              <div>Avg Duration: 2m12s</div>
              <div className="mt-2">Common Actions:</div>
              <div>• Confirm: 75%</div>
              <div>• Reschedule: 15%</div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );

  const renderOnDemandBotContent = () => (
    <>
      <Card>
        <CardContent className="p-6 space-y-4">
          <h3 className="font-semibold">Current Open Slots</h3>
          <div className="space-y-2 text-sm">
            <div>• Today: 2:00 PM, 3:30 PM</div>
            <div>• Tomorrow: 10:00 AM, 1:15 PM</div>
          </div>
          <Button>Update Slots</Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-4">
          <h3 className="font-semibold">Patient List</h3>
          <div className="space-y-2 text-sm">
            <div>• High Priority (5)</div>
            <div>• Standard Priority (10)</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button>Edit List</Button>
            <Button variant="outline">Sort Priority</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-4">
          <h3 className="font-semibold">Call Configuration</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-sm">Max patients to call:</span>
              <Input type="number" className="w-20" defaultValue="15" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm">Call window:</span>
              <Input type="number" className="w-20" defaultValue="2" />
              <span className="text-sm">hours</span>
            </div>
          </div>
          <Button>Start Calls Now</Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <h3 className="font-semibold mb-4">Recent Campaign Results</h3>
          <div className="space-y-2 text-sm">
            <div>Success Rate: 35%</div>
            <div>Slots Filled: 3/5</div>
            <Button className="mt-4">View Details</Button>
          </div>
        </CardContent>
      </Card>
    </>
  );

  return (
    <div className="m-4 md:m-8 space-y-6">
      <div className="flex flex-wrap items-center gap-3 md:gap-4">
        <Button
          variant="outline"
          className="flex items-center gap-2"
          onClick={() => navigate("/bots")}
        >
          <ChevronLeft className="h-4 w-4" />
          Back to Bots
        </Button>
      </div>

      <div className="text-2xl font-semibold">
        {botType === "automated" ? "Appointment-Reminder Bot" : "ASAP-List Bot"}
      </div>

      <div className="space-y-6">
        {botType === "automated"
          ? renderAutomatedBotContent()
          : renderOnDemandBotContent()}
      </div>
    </div>
  );
};

export default BotDetailPage;
